import { useState, useEffect } from "react";
import { Mail, Phone, MapPin } from "lucide-react";
import axios from "axios";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

export default function ContactPage(
{
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Contact | Bandarban University",
}) {
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
	const [formData, setFormData] = useState({
		name: "",
		email: "",
		message: "",
		subject:"",
	});
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState("");
	const [error, setError] = useState("");

	  // Handle input change
	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};
	
	const handleSubmit = async (e) => {
		e.preventDefault();
		setLoading(true);
		setError("");
		setSuccess("");

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				...formData, // include name, email, message
			};
			const response = await axios.post(
				`${import.meta.env.VITE_API_BASE_URL}/contact-form`,
				payload,
				{
				  headers: { "Content-Type": "application/json" },
				}
			);
			if (response.status === 200) {
				setSuccess(response.data?.message || "Message sent successfully 🎉");
				setFormData({ name: "", email: "", message: "", subject: "" });
			} else {
				setError("Something went wrong. Please try again.");
			}
		} catch (err) {
		  setError(err.response?.data?.message || "Server error, please try later.");
		} finally {
		  setLoading(false);
		}
	};
	return (
    <div className="bg-gray-50 text-gray-800">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-700 to-teal-600 text-white py-16 text-center">
        <h1 className="text-4xl font-bold">Contact Us</h1>
        <p className="mt-2 text-lg opacity-90">
          We’d love to hear from you. Get in touch today!
        </p>
      </section>

      {/* Map Section */}
      <section className="w-full h-96">
        <iframe
          title="Google Map"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14783.37626274081!2d92.15118527412412!3d22.131920330993385!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x30ad6b2819643f39%3A0x769c49f9d228c2a9!2sBandarban%20University%20Campus!5e0!3m2!1sen!2sbd!4v1776353218504!5m2!1sen!2sbd"
          className="w-full h-full border-0"
          allowFullScreen=""
          loading="lazy"
        ></iframe>
      </section>

      {/* Contact Info */}
      <section className="max-w-6xl mx-auto py-16 px-6 grid md:grid-cols-3 gap-8 text-center">
        <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition">
          <Mail className="mx-auto w-10 h-10 text-teal-600 mb-4" />
          <h3 className="font-bold text-lg">Email</h3>
          <p className="text-gray-600">info@bubban.ac.bd</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition">
          <Phone className="mx-auto w-10 h-10 text-teal-600 mb-4" />
          <h3 className="font-bold text-lg">Phone</h3>
          <p className="text-gray-600">+88-0131-8305900<br/>+880 1318-305899</p>
        </div>
        <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition">
          <MapPin className="mx-auto w-10 h-10 text-teal-600 mb-4" />
          <h3 className="font-bold text-lg">Address</h3>
          <p className="text-gray-600">K.B Road, Shualok, Post Office: Shualok - 4600, Bandarban Sadar, Bangladesh</p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="max-w-3xl mx-auto py-16 px-6">
        <h2 className="text-2xl font-bold text-center mb-8">Send Us a Message</h2>
		
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-8 space-y-6">
			{/* Success & Error Messages */}
			{success && <p className="text-green-600 text-center">{success}</p>}
			{error && <p className="text-red-600 text-center">{error}</p>}
          <div>
            <label className="block font-medium text-gray-700 mb-2">Name</label>
            <input
              type="text"
			  name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Your Name"
              required
            />
          </div>
          <div>
            <label className="block font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
			  name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="you@example.com"
              required
            />
          </div>
		  <div>
            <label className="block font-medium text-gray-700 mb-2">Subject</label>
            <input
              type="text"
			  name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Subject"
              required
            />
          </div>
          <div>
            <label className="block font-medium text-gray-700 mb-2">Message</label>
            <textarea
              rows="5"
			  name="message"
			  value={formData.message}
			  onChange={handleChange}
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Write your message..."
              required
            ></textarea>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-teal-600 text-white font-semibold py-3 rounded-lg shadow hover:bg-teal-700 transition"
          >
            {loading ? "Sending..." : "Send Message"}
          </button>
        </form>
      </section>
    </div>
  );
}
